/**
 * Alarm history stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared
 * dashboard shell (which supplies AppNav + TitleBar). Server-backed via
 * EntityGrid over sites/{id}/alarms/, defaulting to the "all" status filter
 * since this is the history view. The server sends state-only rows (an
 * `alarm` entity ref {type,id,name} plus status/timestamps); the alarm's
 * label, category, severity, relay, and clearability resolve from the
 * reference catalog by the ref's id at render time.
 *
 * Liveness: the `alarms` snapshot from SiteDashboardLayoutContext changes on
 * every socket alarm raise/clear and on the optimistic removeAlarm dispatch.
 * This view watches that reference and calls EntityGrid's refresh() so the
 * grid stays in sync with realtime events without owning any data fetching.
 *
 * On a phone each row becomes a card instead of a column strip: the alarm
 * leads, the relay loads read underneath it, the timestamps and duration
 * become labelled lines, and an active alarm keeps its Clear action.
 *
 * Contains:
 * - AlarmHistoryView      — route child component (default export)
 * - AlarmHistoryListItem  — mobile card for one alarm row
 */
import { Box, Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { clearSiteAlarmState } from '@sophocles/api/odlApp';
import { useUser } from '@sophocles/app';
import { EntityGrid } from '@sophocles/data-grids';
import { GridDateCell, GridStatusCell, GridTextCell } from '@sophocles/data-grids/components/cells';
import GridMobileCard from '@sophocles/data-grids/components/mobile/GridMobileCard';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { removeAlarmFromSnapshot } from '@sophocles/features/entities/site';
import { selectAlarmCategories, selectAlarmDefinitions } from '@sophocles/features/reference';
import { addToast } from '@sophocles/features/toast';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { formatDateTime, formatDuration } from '@sophocles/utils/formatUtils';
import AdminOnlyAlarmBadge from '@sophocles/views/sites/components/alarms/AdminOnlyAlarmBadge';
import AlarmChip from '@sophocles/views/sites/components/alarms/AlarmChip';
import {
  facesForRelayNumber,
  translateAlarm,
  translateAlarmDescription,
} from '@sophocles/views/sites/components/alarms/alarmLabel';
import AlarmGridFilters from '@sophocles/views/sites/components/SiteDashboard/alarms/AlarmGridFilters';
import {
  alarmDurationMs,
  selectAlarmHistoryCardMeta,
} from '@sophocles/views/sites/components/SiteDashboard/alarms/alarmHistoryCard';
import {
  isAlarmClearable,
  resolveTopCategory,
} from '@sophocles/views/sites/components/SiteDashboard/alarms/alarmUtils';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

const CLEAR_BUTTON_SX = {
  minWidth: 0,
  py: 0.125,
  px: 0.75,
  fontSize: '0.6875rem',
  lineHeight: 1.4,
};

// status is conveyed by the row's own state (Active chip / cleared time), so
// the descriptor's status column is redundant here.
const ALARM_HISTORY_COLUMN_VISIBILITY = { model: { status: false } };

function renderClearAction(row, canClear, commandLocked, t, clearingId, handleClearAlarm) {
  return (
    <Tooltip
      title={
        commandLocked
          ? t('commandActivity.commandsLocked', 'Wait for the queued command to finish before sending another.')
          : !canClear
            ? t('alarms.clearPermissionRequired', 'Requires clear permission')
            : ''
      }
    >
      <span>
        <Button
          size="small"
          variant="outlined"
          disabled={!canClear || commandLocked || clearingId === row.alarm_state_id}
          onClick={event => {
            event.stopPropagation();
            handleClearAlarm(row);
          }}
          sx={CLEAR_BUTTON_SX}
        >
          {t('actions.clear')}
        </Button>
      </span>
    </Tooltip>
  );
}

function renderDurationCell(row, clearable, canClear, commandLocked, t, clearingId, handleClearAlarm) {
  if (!row.resolved_at) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, height: '100%', gap: 1 }}>
        <GridStatusCell label={t('alarms.active', 'Active')} color="bad" />
        {clearable && renderClearAction(row, canClear, commandLocked, t, clearingId, handleClearAlarm)}
      </Box>
    );
  }
  return <GridTextCell value={formatDuration(alarmDurationMs(row))} />;
}

/**
 * @param {object}   props
 * @param {object}   props.row      - alarm history row
 * @param {string}   props.label    - translated alarm name
 * @param {object}   props.def      - alarm definition from the reference catalog (may be {})
 * @param {string}   props.category - resolved top-level category key
 * @param {string}   props.tooltip  - alarm description
 * @param {string}   props.faces    - relay-load names the alarm applies to
 * @param {object}   props.user     - viewing user, for timezone-aware timestamps
 * @param {React.ReactNode} [props.actions] - Clear action, when the row offers one
 */
function AlarmHistoryListItem({ row, label, def, category, tooltip, faces, user, actions }) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();

  const meta = selectAlarmHistoryCardMeta(row, {
    t,
    formatTimestamp: value => formatDateTime(value, user),
    formatDuration,
  });

  return (
    <GridMobileCard
      primary={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', minWidth: 0 }}>
          <AlarmChip
            categoryKey={category}
            label={label}
            tAlarm={tAlarm}
            count={row.alarm_count || 1}
            severity={def.severity}
            tooltip={tooltip}
          />
          <AdminOnlyAlarmBadge def={def} />
        </Box>
      }
      secondary={faces || null}
      meta={meta}
      status={row.resolved_at ? null : <GridStatusCell label={t('alarms.active', 'Active')} color="bad" />}
      actions={actions}
    />
  );
}

function AlarmHistoryView() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const { user } = useUser();
  const theme = useTheme();
  // Matches the breakpoint EntityGrid switches its own mobile layout on, so the
  // desktop-only cell padding below is dropped exactly when the cards appear.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { siteId, relays, alarms, capabilities } = useSiteDashboardLayoutContext();
  const alarmDefs = useSelector(selectAlarmDefinitions);
  const alarmCats = useSelector(selectAlarmCategories);
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  // Customer-type relay-load term ("Panel"/"Zone"/"Face") for the faces column
  // title and per-relay fallback labels; i18n default ("Face"/"Faces") otherwise.
  const relayLoadTerm = useRelayLoadTerm();

  // Reference catalog keyed by id — server rows carry only an `alarm` ref; the
  // alarm's key, category, severity, relay, and clearability resolve from the
  // catalog at render time rather than being denormalized onto each row.
  const defById = useMemo(() => new Map(alarmDefs.map(d => [d.id, d])), [alarmDefs]);
  const catById = useMemo(() => new Map(alarmCats.map(c => [c.id, c])), [alarmCats]);

  const isStaff = capabilities?.is_staff ?? false;
  const canClearAlarms = capabilities?.can_clear_alarms ?? false;

  const [statusFilter, setStatusFilter] = useState('all');
  const [clearingId, setClearingId] = useState(null);

  // ── Liveness: socket alarm events → grid refresh ────────────────────────────

  const { onGridReady: handleGridReady, refresh: refreshGrid } = useGridRefreshRef();
  const prevAlarmsRef = useRef(alarms);

  useEffect(() => {
    if (prevAlarmsRef.current !== alarms) {
      prevAlarmsRef.current = alarms;
      refreshGrid();
    }
  }, [alarms, refreshGrid]);

  // ── Server-side filter params ───────────────────────────────────────────────

  const extraParams = useMemo(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    return params;
  }, [statusFilter]);

  // ── Clear action ────────────────────────────────────────────────────────────

  const handleClearAlarm = useCallback(
    async row => {
      const def = defById.get(row.alarm?.id) ?? {};
      setClearingId(row.alarm_state_id);
      try {
        const outcome = await runWrite(() => clearSiteAlarmState(siteId, row.alarm_state_id), {
          requiresCommand: true,
        });
        if (!outcome.executed) return;
        dispatch(removeAlarmFromSnapshot(siteId, row.alarm_state_id));
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message: `${translateAlarm(tAlarm, def.key, def.display_name || def.key)} ${t('alarms.cleared', 'cleared')}`,
            level: 'success',
            dismissible: true,
          }),
        );
      } catch {
        dispatch(
          addToast({
            title: t('general.error', 'Error'),
            message: t('alarms.clearFailed', 'Failed to clear alarm'),
            level: 'error',
            dismissible: true,
          }),
        );
      } finally {
        setClearingId(null);
      }
    },
    [defById, dispatch, runWrite, siteId, t, tAlarm],
  );

  // ── FE column overrides applied on top of the spec type defaults ────────────

  const fieldOverrides = useMemo(
    () => ({
      faces: {
        headerName: relayLoadTerm.plural,
        renderCell: p => {
          const def = defById.get(p.row.alarm?.id) ?? {};
          return <GridTextCell value={facesForRelayNumber(def.relay_number, relays, t, relayLoadTerm.singular)} />;
        },
      },
      alarm_label: {
        headerName: t('alarms.alarm', 'Alarm'),
        renderCell: p => {
          const def = defById.get(p.row.alarm?.id) ?? {};
          const category = resolveTopCategory(catById, def.category_id);
          const categoryKey = category?.key ?? 'other';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', minWidth: 0 }}>
              <AlarmChip
                categoryKey={categoryKey}
                label={translateAlarm(tAlarm, def.key, def.display_name || def.key)}
                tAlarm={tAlarm}
                count={p.row.alarm_count || 1}
                severity={def.severity}
                tooltip={translateAlarmDescription(tAlarm, def, category)}
              />
              <AdminOnlyAlarmBadge def={def} />
            </Box>
          );
        },
      },
      duration: {
        renderCell: p => {
          const def = defById.get(p.row.alarm?.id) ?? {};
          const categoryKey = resolveTopCategory(catById, def.category_id)?.key ?? 'other';
          const clearable = isAlarmClearable(def, { excessive: p.row.excessive, isStaff, categoryKey });
          const canClear = clearable && canClearAlarms;
          return renderDurationCell(p.row, clearable, canClear, commandLocked, t, clearingId, handleClearAlarm);
        },
      },
      cleared_by: {
        renderCell: p => <GridTextCell value={p.value} />,
      },
      triggered_at: {
        renderCell: p => <GridDateCell value={p.value} user={user} />,
      },
      resolved_at: {
        renderCell: p => <GridDateCell value={p.value} user={user} />,
      },
    }),
    [
      relayLoadTerm,
      t,
      tAlarm,
      relays,
      commandLocked,
      clearingId,
      handleClearAlarm,
      user,
      defById,
      catById,
      isStaff,
      canClearAlarms,
    ],
  );

  // ── Mobile card ─────────────────────────────────────────────────────────────

  // Every column this grid shows is derived from the alarm reference catalog,
  // so the card resolves the same definition the columns do and hands the
  // resolved values in — the card itself stays free of catalog lookups.
  const renderMobileCard = useCallback(
    row => {
      const def = defById.get(row.alarm?.id) ?? {};
      const category = resolveTopCategory(catById, def.category_id);
      const categoryKey = category?.key ?? 'other';
      const clearable = isAlarmClearable(def, { excessive: row.excessive, isStaff, categoryKey });
      const canClear = clearable && canClearAlarms;

      return (
        <AlarmHistoryListItem
          row={row}
          def={def}
          category={categoryKey}
          label={translateAlarm(tAlarm, def.key, def.display_name || def.key)}
          tooltip={translateAlarmDescription(tAlarm, def, category)}
          faces={facesForRelayNumber(def.relay_number, relays, t, relayLoadTerm.singular)}
          user={user}
          actions={
            !row.resolved_at && clearable
              ? renderClearAction(row, canClear, commandLocked, t, clearingId, handleClearAlarm)
              : null
          }
        />
      );
    },
    [
      defById,
      catById,
      isStaff,
      canClearAlarms,
      tAlarm,
      relays,
      t,
      relayLoadTerm,
      user,
      commandLocked,
      clearingId,
      handleClearAlarm,
    ],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <EntityGrid
        basePath={`sites/${siteId}/alarms`}
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          showSearch: true,
          searchPlaceholder: t('general.search', 'Search...'),
          countNoun: t('alarms.countNoun', 'alarms'),
          showGridSettings: false,
          toolbarVariant: 'flat',
          toolbarContent: <AlarmGridFilters statusFilter={statusFilter} onStatusChange={setStatusFilter} t={t} />,
          onGridReady: handleGridReady,
          columnVisibility: ALARM_HISTORY_COLUMN_VISIBILITY,
          mobile: { renderCard: renderMobileCard },
          gridProps: {
            density: 'compact',
            columnHeaderHeight: 38,
            disableColumnMenu: true,
            disableRowSelectionOnClick: true,
            hideFooter: true,
            localeText: { noRowsLabel: t('general.noResultsMessage') },
            sx: {
              flex: 1,
              minHeight: 0,
              // sx merges by key, so a cell rule here replaces the grid's own
              // mobile one wholesale — including the overrides that let a card
              // grow past one desktop row. Desktop cells only.
              ...(isMobile
                ? {}
                : {
                    // Auto row height hugs the content, so give cells vertical
                    // padding or the chips sit flush against the row edges.
                    '& .MuiDataGrid-cell': { alignItems: 'center', py: 0.75 },
                  }),
            },
          },
        }}
      />
    </Box>
  );
}

export default AlarmHistoryView;
